import React, { useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Grid, Container, Typography } from '@material-ui/core';
import * as Yup from 'yup';
import i18n from 'i18n-js';
import { FormChanges } from 'utils';
import { Formik, Field } from 'formik';
import useStyles from './Details.styled';
import { TextField } from 'components/FormFields';
import { VehiclesGroupsActions } from 'state/modules/vehiclesGroups';
import { TimezoneSelect, ReservationsRulesSelect } from 'components/AsyncFields';

const generateSchema = (requiresRuleset) =>
  Yup.object().shape({
    name: Yup.string().required(i18n.t('common.required_field')),
    time_zone: Yup.string().required(i18n.t('common.required_field')),
    rules_set_id: requiresRuleset
      ? Yup.number().required(i18n.t('common.required_field'))
      : Yup.number(),
  });

const ServiceAreaDetails = ({ formRef, errors, step, exit, onSave, rules }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const hasReservations = useSelector(
    (state) => state.config?.settings?.core?.reservation?.enabled || false
  );
  const canCreateReservation = useSelector(
    (state) => state.config?.settings?.core?.reservation?.create || false
  );
  const requiresRuleset = hasReservations && canCreateReservation && rules?.length > 0;
  const formSchema = useMemo(() => generateSchema(requiresRuleset), [requiresRuleset]);

  useEffect(() => {
    if (
      hasReservations &&
      rules?.length === 1 &&
      !step?.id &&
      !formRef?.current?.values?.rules_set_id &&
      !requiresRuleset
    ) {
      formRef.current.setFieldValue('rules_set_id', rules[0]?.id);
    }
  }, [rules, formRef]);

  return (
    <Box className={classes.container}>
      <Container className="h-100">
        <Grid container direction="row" justifyContent="center" alignItems="center">
          <Grid item xs={6}>
            <Formik
              enableReinitialize
              innerRef={formRef}
              initialValues={{
                name: step?.name || '',
                time_zone: step?.time_zone || '',
                rules_set_id: step?.rules_set_id || undefined,
              }}
              validationSchema={formSchema}
              onSubmit={(values) => {
                let payload = {};

                if (step?.id) {
                  payload = { ...values };
                } else {
                  payload = {
                    ...values,
                    group_type: 'OWNER',
                  };
                }
                dispatch(VehiclesGroupsActions.fetchCreateVehiclesGroups(payload, 'details', exit));
              }}
            >
              {({ handleSubmit }) => (
                <form noValidate onSubmit={handleSubmit}>
                  <Grid container>
                    <Grid item xs={12}>
                      <Typography gutterBottom component="div">
                        <Box data-cy="step-title" fontWeight="fontWeightBold" fontSize={14} my={1}>
                          {i18n.t('vehicle_groups.service_area_general_info')}
                        </Box>
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Field
                        type="text"
                        name="name"
                        component={TextField}
                        asyncErrors={errors?.name}
                        label={i18n.t('vehicle_groups.identifier_name')}
                      />
                      <FormChanges initialValues={{ name: step?.name || '' }} onSave={onSave} />
                    </Grid>
                    <Grid item xs={12}>
                      <Field
                        name="time_zone"
                        component={TimezoneSelect}
                        label={i18n.t('customer_groups.timezone')}
                      />
                    </Grid>
                    {requiresRuleset ? (
                      <Grid item xs={12}>
                        <Field
                          required
                          type="driver"
                          name="rules_set_id"
                          id="DriversReservationRules"
                          component={ReservationsRulesSelect}
                          label={i18n.t('vehicle_groups.reservation_ruleset')}
                        />
                      </Grid>
                    ) : null}
                  </Grid>
                </form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default ServiceAreaDetails;
